import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  type QueryDocumentSnapshot,
  type DocumentData,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Contact } from '../types/contact';
import { segmentService } from './segmentService';
import { assertNotImpersonating } from '../utils/impersonationGuard';


class ContactService {
  async createContact(contactData: Omit<Contact, 'id' | 'addedAt' | 'lastChanged'>): Promise<string> {
    assertNotImpersonating('create contact');
    try {
      // Normalize email to lowercase for consistent storage and querying
      const normalizedEmail = contactData.email.trim().toLowerCase();
      
      console.log('📝 Creating new contact:', {
        email: normalizedEmail,
        workspaceId: contactData.workspaceId,
        firstName: contactData.firstName,
        lastName: contactData.lastName
      });
      
      // Double-check email doesn't exist before creating
      const emailExists = await this.checkEmailExists(normalizedEmail, contactData.workspaceId);
      if (emailExists) {
        console.error('❌ Cannot create contact - email already exists:', normalizedEmail);
        throw new Error('A contact with this email address already exists in this workspace');
      }
      
      // Ensure segments array is initialized
      const contactWithSegments = {
        ...contactData,
        email: normalizedEmail,
        segments: contactData.segments || []
      };

      const docRef = await addDoc(collection(db, 'contacts'), {
        ...contactWithSegments,
        addedAt: serverTimestamp(),
        lastChanged: serverTimestamp()
      });
      
      console.log('✅ Contact created with ID:', docRef.id);
      
      // contact_added automations are triggered server-side by the
      // `onContactCreated` Firestore trigger (functions/automationTriggers.js).
      //
      // The client used to call `automationExecutionEngine.handleContactAdded`
      // here as well, which meant two independent code paths created an execution
      // for the same contact. The dedupe key added to both creators collapses
      // that for non-repeating automations, but an automation with
      // `allowRepetition: true` has no stable key to dedupe on, so both
      // executions would survive and the contact would receive the email twice.
      //
      // Removing the client call also drops several Firestore reads per contact
      // insert (automation lookup, contact re-read, existing-execution query),
      // which matters on bulk imports.
      
      // Evaluate contact against dynamic segments (async, don't wait)
      if (contactData.workspaceId) {
        // Use setTimeout to ensure contact is fully saved before evaluation
        setTimeout(() => {
          segmentService.evaluateContactForSegments(docRef.id, contactData.workspaceId).catch(err => {
            console.error('Error evaluating contact for segments:', err);
          });
        }, 500);
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating contact:', error);
      // Re-throw the error with the original message if it's our duplicate error
      if (error instanceof Error && error.message.includes('already exists')) {
        throw error;
      }
      throw new Error('Failed to create contact');
    }
  }

  async getContact(contactId: string): Promise<Contact | null> {
    try {
      const docRef = doc(db, 'contacts', contactId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Contact;
      }
      return null;
    } catch (error) {
      console.error('Error getting contact:', error);
      throw new Error('Failed to get contact');
    }
  }

  async addTagToContact(contactId: string, tagId: string): Promise<void> {
    assertNotImpersonating('tag contact');
    try {
      const contact = await this.getContact(contactId);
      if (!contact) {
        throw new Error('Contact not found');
      }

      const updatedTags = [...(contact.tags || []), tagId];
      const workspaceId = contact.workspaceId;
      
      await updateDoc(doc(db, 'contacts', contactId), {
        tags: updatedTags,
        lastChanged: serverTimestamp()
      });

      // Re-evaluate contact against dynamic segments
      if (workspaceId) {
        segmentService.evaluateContactForSegments(contactId, workspaceId).catch(err => {
          console.error('Error evaluating contact for segments:', err);
        });
      }
    } catch (error) {
      console.error('Error adding tag to contact:', error);
      throw new Error('Failed to add tag to contact');
    }
  }

  async removeTagFromContact(contactId: string, tagId: string): Promise<void> {
    assertNotImpersonating('untag contact');
    try {
      const contact = await this.getContact(contactId);
      if (!contact) {
        throw new Error('Contact not found');
      }

      const updatedTags = (contact.tags || []).filter(tag => tag !== tagId);
      const workspaceId = contact.workspaceId;
      
      await updateDoc(doc(db, 'contacts', contactId), {
        tags: updatedTags,
        lastChanged: serverTimestamp()
      });

      // Re-evaluate contact against dynamic segments
      if (workspaceId) {
        segmentService.evaluateContactForSegments(contactId, workspaceId).catch(err => {
          console.error('Error evaluating contact for segments:', err);
        });
      }
    } catch (error) {
      console.error('Error removing tag from contact:', error);
      throw new Error('Failed to remove tag from contact');
    }
  }

  async updateContact(contactId: string, updates: { [key: string]: any }): Promise<void> {
    assertNotImpersonating('update contact');
    try {
      // Get current contact to get workspaceId
      const currentContact = await this.getContact(contactId);
      const workspaceId = currentContact?.workspaceId || updates.workspaceId;

      await updateDoc(doc(db, 'contacts', contactId), {
        ...updates,
        lastChanged: serverTimestamp()
      });

      // Re-evaluate contact against dynamic segments if workspaceId is available
      if (workspaceId) {
        segmentService.evaluateContactForSegments(contactId, workspaceId).catch(err => {
          console.error('Error evaluating contact for segments:', err);
        });
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      throw new Error('Failed to update contact');
    }
  }

  async deleteContact(contactId: string): Promise<void> {
    assertNotImpersonating('delete contact');
    try {
      await deleteDoc(doc(db, 'contacts', contactId));
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw new Error('Failed to delete contact');
    }
  }

  async getContactsByTag(tagId: string, workspaceId: string): Promise<Contact[]> {
    try {
      const q = query(
        collection(db, 'contacts'),
        where('tags', 'array-contains', tagId),
        where('workspaceId', '==', workspaceId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Contact[];
    } catch (error) {
      console.error('Error getting contacts by tag:', error);
      throw new Error('Failed to get contacts by tag');
    }
  }

  async getContactsBySegment(segmentId: string, workspaceId: string): Promise<Contact[]> {
    try {
      const q = query(
        collection(db, 'contacts'),
        where('segments', 'array-contains', segmentId),
        where('workspaceId', '==', workspaceId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Contact[];
    } catch (error) {
      console.error('Error getting contacts by segment:', error);
      throw new Error('Failed to get contacts by segment');
    }
  }

  async getAllContacts(userId: string): Promise<Contact[]> {
    try {
      // Query all contacts for a user across all workspaces
      const q = query(
        collection(db, 'contacts'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const contacts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Contact[];
      
      // Sort by addedAt in descending order (newest first)
      contacts.sort((a, b) => {
        const aTime = a.addedAt instanceof Timestamp ? a.addedAt.toMillis() : 0;
        const bTime = b.addedAt instanceof Timestamp ? b.addedAt.toMillis() : 0;
        return bTime - aTime;
      });
      
      return contacts;
    } catch (error) {
      console.error('Error getting all contacts:', error);
      throw new Error('Failed to get all contacts');
    }
  }

  async getContacts(userId: string, workspaceId: string): Promise<Contact[]> {
    try {
      // Query contacts by both userId and workspaceId for proper isolation
      const q = query(
        collection(db, 'contacts'),
        where('userId', '==', userId),
        where('workspaceId', '==', workspaceId)
      );

      const querySnapshot = await getDocs(q);
      const contacts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Contact[];

      // Sort in memory instead of using orderBy
      return contacts.sort((a, b) => {
        const aDate = a.addedAt?.toDate?.() || new Date(0);
        const bDate = b.addedAt?.toDate?.() || new Date(0);
        return bDate.getTime() - aDate.getTime(); // Descending order
      });
    } catch (error) {
      console.error('Error getting contacts:', error);
      throw new Error('Failed to get contacts');
    }
  }

  /**
   * Fetch one page of contacts with server-side cursor pagination.
   *
   * Uses Firestore `orderBy('addedAt', dir) + limit(pageSize) + startAfter(cursor)`
   * so only `pageSize` documents are read per call — dramatically cheaper than
   * fetching all contacts and slicing client-side.
   *
   * @param userId      - Owning user UID
   * @param workspaceId - Workspace to scope the query to (pass null to load all workspaces)
   * @param options.pageSize    - Documents per page (default 50)
   * @param options.cursor      - Last document from the previous page (null = first page)
   * @param options.sortDirection - 'asc' | 'desc' on addedAt (default 'desc')
   */
  async getContactsPage(
    userId: string,
    workspaceId: string | null,
    options: {
      pageSize?: number;
      cursor?: QueryDocumentSnapshot<DocumentData> | null;
      sortDirection?: 'asc' | 'desc';
    } = {}
  ): Promise<{
    contacts: Contact[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
    hasMore: boolean;
  }> {
    const { pageSize = 50, cursor = null, sortDirection = 'desc' } = options;
    try {
      const contactsRef = collection(db, 'contacts');

      // Try the ordered query first (requires composite index).
      // If it fails (missing index), fall back to an unordered query and
      // sort client-side — same behaviour as the original getContacts().
      const buildConstraints = (withOrder: boolean) => {
        const c: any[] = [where('userId', '==', userId)];
        if (workspaceId) c.push(where('workspaceId', '==', workspaceId));
        if (withOrder) c.push(orderBy('addedAt', sortDirection));
        c.push(limit(pageSize + 1));
        if (cursor && withOrder) c.push(startAfter(cursor));
        return c;
      };

      let snapshot;
      try {
        snapshot = await getDocs(query(contactsRef, ...buildConstraints(true)));
      } catch (indexErr) {
        console.warn('[contactService] Composite index missing, falling back to unordered query:', indexErr);
        snapshot = await getDocs(query(contactsRef, ...buildConstraints(false)));
      }

      const hasMore = snapshot.docs.length > pageSize;
      const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

      const contacts = docs
        .map(d => ({ id: d.id, ...d.data() } as Contact))
        .filter(c => !!c.email)
        .sort((a, b) => {
          const aMs = a.addedAt?.toMillis?.() ?? 0;
          const bMs = b.addedAt?.toMillis?.() ?? 0;
          return sortDirection === 'desc' ? bMs - aMs : aMs - bMs;
        });

      const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;

      return { contacts, lastDoc, hasMore };
    } catch (error) {
      console.error('Error getting contacts page:', error);
      throw new Error('Failed to get contacts');
    }
  }

  async checkEmailExists(email: string, workspaceId: string): Promise<boolean> {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      console.log('🔍 Checking if email exists:', { email: normalizedEmail, workspaceId });
      
      const contactsRef = collection(db, 'contacts');
      
      // First, try exact lowercase match
      const exactQuery = query(
        contactsRef,
        where('workspaceId', '==', workspaceId),
        where('email', '==', normalizedEmail)
      );

      const exactSnapshot = await getDocs(exactQuery);
      
      if (!exactSnapshot.empty) {
        return true;
      }

      // Exact-match query on the normalised email is the only path.
      // The previous fallback downloaded ALL contacts in the workspace for a
      // case-insensitive JS comparison — O(n) reads per import row.
      // All new contacts are stored with a lowercase email (normalizedEmail
      // above), so the exact query is sufficient. Legacy mixed-case contacts
      // can be migrated via normalizeWorkspaceEmails().
      return false;
    } catch (error) {
      console.error('❌ Error checking email existence:', error);
      throw new Error('Failed to check email existence');
    }
  }

  /**
   * Normalizes all email addresses in a workspace to lowercase
   * This is useful for migrating existing data to the new lowercase format
   */
  async normalizeWorkspaceEmails(workspaceId: string): Promise<{ updated: number; skipped: number }> {
    assertNotImpersonating('normalize contact emails');
    try {
      console.log('🔄 Starting email normalization for workspace:', workspaceId);
      
      const contactsRef = collection(db, 'contacts');
      const q = query(contactsRef, where('workspaceId', '==', workspaceId));
      const snapshot = await getDocs(q);
      
      let updated = 0;
      let skipped = 0;
      
      const updatePromises = snapshot.docs.map(async (docSnapshot) => {
        const contact = docSnapshot.data();
        const currentEmail = contact.email || '';
        const normalizedEmail = currentEmail.trim().toLowerCase();
        
        if (currentEmail !== normalizedEmail) {
          await updateDoc(doc(db, 'contacts', docSnapshot.id), {
            email: normalizedEmail,
            lastChanged: serverTimestamp()
          });
          updated++;
          console.log(`✅ Normalized: ${currentEmail} → ${normalizedEmail}`);
        } else {
          skipped++;
        }
      });
      
      await Promise.all(updatePromises);
      
      console.log('✅ Email normalization complete:', { updated, skipped, total: snapshot.size });
      return { updated, skipped };
    } catch (error) {
      console.error('❌ Error normalizing emails:', error);
      throw new Error('Failed to normalize emails');
    }
  }
}

export const contactService = new ContactService();
